import moment from "moment";
import {
  QUESTION_TYPE,
  THUMBNAIL_TYPE,
  NON_CLASSIFIED,
  DATE_TIME_FORMAT,
  FUNNEL_TYPE_FILTER,
  DATE_REVERSE_FORMAT,
  WEBSITE_BASE_TAB_ROUTE,
  WISTIA_EMBED_URL_REGEX,
  YOUTUBE_EMBED_URL_REGEX,
  LS_TRACKING_RESULT_HASH,
  SYSTEM_HEALTH_ALERT_ISSUE,
  SYSTEM_HEALTH_ALERT_ACTION,
  CREATIVE_ASSET_INSIGHT_TYPE,
  LEAD_AGENT_TIMESTAMP_SESSION_EXPIRY
} from "../components/strings";
import { showError } from "./toast-service";
import { updateAsset, getFunnelVersionsById } from "./api-declaration";

const {
  NODE_ENV,
  PUBLIC_URL,
  REACT_APP_STATIC_URL,
  REACT_APP_ENVIRONMENT,
  REACT_APP_ASSET_CDN_URL,
  REACT_APP_CUSTOMER_SITE_URL
} = process.env;

export const transformOption = (str) => str.toLowerCase();

export const filterTypeOptions = [
  transformOption(FUNNEL_TYPE_FILTER.ALL),
  transformOption(FUNNEL_TYPE_FILTER.LEAD),
  transformOption(FUNNEL_TYPE_FILTER.EMPLOYEE)
];

export const isValidHttpUrl = (urlString) => {
  var urlPattern = new RegExp(
    "^(https?:\\/\\/)?" + // validate protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // validate domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // validate OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // validate port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // validate query string
      "(\\#[-a-z\\d_]*)?$",
    "i"
  ); // validate fragment locator
  return urlPattern.test(urlString);
};

export const colorCodeRegex = /^#(?:[0-9a-f]{3}){1,2}$/i;

export const updateAssetEntry = async (assetData) => {
  return updateAsset(assetData, assetData._id);
};

export const checkStringDataType = (item) => typeof item === "string";

export const generateSiteUrl = (domain, subDomain) => {
  return domain && subDomain
    ? REACT_APP_ENVIRONMENT === "dev"
      ? `${REACT_APP_STATIC_URL}?domain=${domain}&subDomain=${subDomain}`
      : `https://${subDomain}.${domain}`
    : null;
};

export const generateCustomerSiteUrl = ({ domain, tabRoute = "/" }) => {
  const _tabRoute = tabRoute === WEBSITE_BASE_TAB_ROUTE ? "" : tabRoute;
  return domain
    ? REACT_APP_ENVIRONMENT === "dev"
      ? `${REACT_APP_CUSTOMER_SITE_URL}?domain=${domain}&tabRoute=${tabRoute}`
      : `https://${domain}${_tabRoute}`
    : null;
};

export const generateCustomerSitePreviewUrl = ({ templateId, tabRoute }) => {
  if (!templateId) return null;
  const hasCustomRoute = tabRoute && tabRoute !== WEBSITE_BASE_TAB_ROUTE;
  const path = hasCustomRoute ? tabRoute : "";
  return `${REACT_APP_CUSTOMER_SITE_URL}${path}?templateId=${templateId}`;
};

export function handleTestMode(url) {
  return url && `${url}${url.includes("?") ? "&" : "?"}testMode=true`;
}

export const handleSelectFieldValue = (selectedValue, options) => {
  if (selectedValue && checkStringDataType(selectedValue)) {
    const entity = options.find((opt) => opt.value === selectedValue);
    return {
      label: entity?.label,
      value: selectedValue
    };
  } else {
    return selectedValue;
  }
};

export function bytesToSize(bytes) {
  var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "n/a";
  var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  if (i === 0) return bytes + " " + sizes[i];
  return (bytes / Math.pow(1024, i)).toFixed(1) + " " + sizes[i];
}

export const openColorPicker = (colorPickerRef) => {
  colorPickerRef.current?.click();
};

export const handleColorChange = (() => {
  let timer; // variable persisted here
  return ({ target: { name, value } }, handleValue) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      handleValue(name, value, { shouldDirty: true });
    }, 200);
  };
})();

export const resetAnswers = (resource) =>
  resource.funnelQA.length
    ? resource.funnelQA.map((question) => {
        if (question.type === QUESTION_TYPE.SLIDER) {
          // a slider has no answer options, its only answer keeps the next step
          question.answers = (question.answers || [])
            .slice(0, 1)
            .map(({ _id, answerAction }) => ({ _id, answerAction }));
        }
        return question;
      })
    : [];

export const generateDateTime = (_date, _format = DATE_TIME_FORMAT) =>
  !_date ? "---" : moment(_date).format(_format);

export const generateDate = (_date) =>
  !_date ? "---" : moment(_date).format(DATE_REVERSE_FORMAT);

export function generateUniqueNumber(length) {
  if (typeof length !== "number" || length <= 0) {
    throw new Error("Invalid length. Length must be a positive number.");
  }
  let result = "";
  const characters = "0123456789";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randomIndex);
  }
  return parseInt(result, 10);
}

export const getEuroCurrencyData = (data) =>
  data?.toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2
  });

export const getFormattedNumber = (data) => data?.toLocaleString("de-DE");

export const getCurrencyData = (currency, data) =>
  data?.toLocaleString("de-DE", {
    style: "currency",
    currency,
    minimumFractionDigits: 2
  });

export const leadAgentSessionExpiry = () =>
  new Date(Date.now() - LEAD_AGENT_TIMESTAMP_SESSION_EXPIRY);

export const removeDuplicatedIds = (question) => {
  delete question._id;
  question.answers.forEach((answer) => {
    delete answer._id;
  });
};

export const generateContactCloudUrl = ({ meta }) => {
  const { tenantId, userId, productId, jobId } = meta;
  const contactCloudDomain = "api.contact-cloud.io";
  const baseUrl =
    NODE_ENV === "production"
      ? `https://${contactCloudDomain}`
      : `https://dev.${contactCloudDomain}`;
  const webhookUrl = jobId
    ? `application/${tenantId}/${userId}/${jobId}`
    : `${tenantId}/${userId}/${productId}`;
  return `${baseUrl}/${webhookUrl}`;
};

export const generateScriptUrl = ({ workspace, scriptProp, localFileName }) =>
  workspace[scriptProp]?.key
    ? `${REACT_APP_ASSET_CDN_URL}/${workspace[scriptProp].key}`
    : `${PUBLIC_URL}/agent-scripts/${localFileName}`;

export const removeWhiteSpaces = (_string) => _string.replace(/\s/g, "");

export const generateAssetFeedSpecs = ({ t, data, insights }) => {
  const { BODY_ASSET, TITLE_ASSET, DESCRIPTION_ASSET } =
    CREATIVE_ASSET_INSIGHT_TYPE;
  return [
    {
      collection: data?.titles || [],
      headerText: t("adEvaluation.titlesLabel"),
      insights: insights?.filter((item) => item.assetType === TITLE_ASSET) || []
    },
    {
      collection: data?.descriptions || [],
      headerText: t("adEvaluation.descriptionsLabel"),
      insights:
        insights?.filter((item) => item.assetType === DESCRIPTION_ASSET) || []
    },
    {
      collection: data?.bodies || [],
      headerText: t("adEvaluation.bodiesLabel"),
      insights: insights?.filter((item) => item.assetType === BODY_ASSET) || []
    }
  ];
};

export const generateSystemActionText = (action) => {
  let generatedText;
  switch (action) {
    case SYSTEM_HEALTH_ALERT_ACTION.FACEBOOK_AD_LEAD_FAILED:
      generatedText = "facebook ad lead creation";
      break;
    case SYSTEM_HEALTH_ALERT_ACTION.CC_DEAL_UPDATE_FAILED:
      generatedText = "lead updation on contact cloud";
      break;
    case SYSTEM_HEALTH_ALERT_ACTION.SPLIT_TEST_ROLLOUT_FAILED:
      generatedText = "split-test roll-out (Split-Tests page: retry)";
      break;
    case SYSTEM_HEALTH_ALERT_ACTION.SPLIT_TEST_PROPOSAL_FAILED:
      generatedText = "split-test proposal (Split-Tests page: create manually)";
      break;
    default:
      generatedText = "";
  }
  return generatedText;
};

export const generateSystemIssueText = (action) => {
  const { UNKNOWN, INVALID_ACCESS_TOKEN, API_LIMIT_REACHED } =
    SYSTEM_HEALTH_ALERT_ISSUE;
  let generatedText;
  switch (action) {
    case UNKNOWN:
      generatedText = "Unknown";
      break;
    case INVALID_ACCESS_TOKEN:
      generatedText = "Access Token";
      break;
    case API_LIMIT_REACHED:
      generatedText = "API Limit Reached";
      break;
    default:
      generatedText = "";
  }
  return generatedText;
};

export const parseResourceSrc = (resourceObj) =>
  resourceObj?.importUrl
    ? resourceObj.importUrl
    : resourceObj?.key
      ? `${REACT_APP_ASSET_CDN_URL}/${resourceObj.key}/${THUMBNAIL_TYPE.ANSWER_OPTION_ICON}`
      : "";

export const parsePreviewResourceSrc = (resourceObj) =>
  resourceObj?.importUrl
    ? resourceObj.importUrl
    : resourceObj?.key
      ? `${REACT_APP_ASSET_CDN_URL}/${resourceObj.key}`
      : "";

export const fetchAndSetFunnelVersions = async (t, paramsId, setter) => {
  try {
    const response = await getFunnelVersionsById(paramsId);
    const versions = response.data.data;
    versions.currentVersion = versions.allVersions.find(
      ({ _id }) => _id === versions.currentVersion.activeVersion
    );
    setter(versions);
  } catch (error) {
    showError(error.response?.data?.message || t("toastMessage.fetchVersion"));
  }
};

export const formatLeadServiceInsightsForBarChart = ({ collection = [] }) => {
  let total = 0;
  const result = Object.fromEntries(
    collection.map(({ _id, count }) => {
      // ignoring non classified (COMPLETED)
      if (_id !== NON_CLASSIFIED) {
        total += count;
        return [LS_TRACKING_RESULT_HASH[_id], count];
      }
      return {};
    })
  );
  result.total = total;
  return result;
};

export const validateEmbedUrl = (url) => {
  return YOUTUBE_EMBED_URL_REGEX.test(url) || WISTIA_EMBED_URL_REGEX.test(url);
};

export const replaceAllUnderscore = (str) =>
  str?.replaceAll("_", " ")?.toLowerCase();

// Function to block unwanted characters on key press. Allowed keys: digits (0-9), the plus sign ('+') only as the first character
export const checkPhoneNumFieldFormatOnKeyDown = (e) => {
  const { key, target, ctrlKey, metaKey } = e;
  const isDigit = /[0-9]/.test(key);
  const isLeadingPlus =
    key === "+" && target.selectionStart === 0 && !target.value.startsWith("+");

  // 1. Allow control keys (Backspace, Arrows, etc.)
  if (
    ctrlKey ||
    metaKey ||
    ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"].includes(
      key
    )
  ) {
    return;
  }
  // 2. Allow the valid keys (Digits OR a leading Plus)
  if (isDigit || isLeadingPlus) {
    return;
  }
  // 3. Block everything else
  e.preventDefault();
};

export const cleanedQuillContent = (value) => {
  return ["<p><br></p>", "<p> </p>"].includes(value) ? "" : value;
};

function _hexToHsl(hex) {
  hex = hex.replace(/^#/, "");
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;

  let max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // gray
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        break;
    }
    h /= 6;
  }
  return { h, s, l };
}

function _hslToHex(h, s, l) {
  function _hue2rgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    let q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    let p = 2 * l - q;
    r = _hue2rgb(p, q, h + 1 / 3);
    g = _hue2rgb(p, q, h);
    b = _hue2rgb(p, q, h - 1 / 3);
  }

  const _toHex = (x) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${_toHex(r)}${_toHex(g)}${_toHex(b)}`;
}

export const getDarkerShade = (hex, factor = 0.5) => {
  let { h, s, l } = _hexToHsl(hex);
  l = Math.max(0, l - factor);
  return _hslToHex(h, s, l);
};
